<script setup lang="ts">
/**
 * Modal Close Button
 *
 * A small X button building block for use inside modal headers.
 * Emits a standard click event — the consumer wires it to close the modal.
 *
 * @example
 * <UiModalCloseButton @click="isOpen = false" />
 */
import { useComponentClasses } from "../../composables";

/**
 * ModalCloseButton component props.
 */
export interface ModalCloseButtonProps {
  /** Accessible label for screen readers */
  ariaLabel?: string;
}

const { ariaLabel = "Close" } = defineProps<ModalCloseButtonProps>();

const { element, root } = useComponentClasses("ui-modal-close-button");

const rootClass = root();
</script>

<template>
  <button type="button" :class="rootClass" :aria-label="ariaLabel">
    <svg
      :class="element('icon')"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  </button>
</template>

<style scoped>
.ui-modal-close-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: var(--ui-radius-sm);
  background: transparent;
  color: var(--ui-modal-close-color, var(--ui-color-text-muted));
  cursor: pointer;
  transition: color 150ms ease-in-out;
}

.ui-modal-close-button:hover {
  color: var(--ui-modal-close-color-hover, var(--ui-color-text));
}

.ui-modal-close-button:focus-visible {
  @mixin focus-ring;
}

.ui-modal-close-button__icon {
  width: 1.5rem;
  height: 1.5rem;
}
</style>
